'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
};

const MobileCollectionCarousel = ({ children }: Props) => {
  const slides = useMemo(() => React.Children.toArray(children), [children]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const mostVisibleEntry = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!mostVisibleEntry) return;

        const nextIndex = Number(
          (mostVisibleEntry.target as HTMLElement).dataset.slideIndex || 0
        );
        setActiveIndex(nextIndex);
      },
      {
        root: container,
        threshold: [0.4, 0.6, 0.8],
      }
    );

    slideRefs.current.forEach(slide => {
      if (slide) observer.observe(slide);
    });

    return () => {
      observer.disconnect();
    };
  }, [slides.length]);

  const scrollToSlide = (index: number) => {
    const slide = slideRefs.current[index];
    if (!slide) return;

    slide.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
    setActiveIndex(index);
  };

  return (
    <div className="w-full sm:hidden">
      <div
        ref={containerRef}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((child, index) => (
          <div
            key={index}
            ref={element => {
              slideRefs.current[index] = element;
            }}
            data-slide-index={index}
            className="min-w-[88%] snap-start py-1"
          >
            {child}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => scrollToSlide(index)}
            className={`h-2.5 w-2.5 rounded-full transition-all duration-200 ${
              index === activeIndex ? 'scale-110 bg-[#223A27]' : 'bg-[#d6d0c3]'
            }`}
            aria-label={`Go to collection slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileCollectionCarousel;
